import React from 'react';
import Slider from 'react-slick';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, Button, IconButton } from '@material-ui/core';
import LanguageIcon from '@material-ui/icons/Language';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import WorkIcon from '@material-ui/icons/Work';
import { useTranslation } from 'react-i18next';

const backgroundImage = '/assets/img/background-0.jpg';

const useStyles = makeStyles((theme) => ({
  section: {
    position: 'relative',
    background: `
      radial-gradient(circle at 25% 25%, rgba(0, 245, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(199, 125, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(224, 170, 255, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0a0a0f 0%, #151521 50%, #1a1a2e 100%),
      url(${backgroundImage})
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    color: '#ffffff',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(45deg, rgba(0, 245, 255, 0.05), rgba(199, 125, 255, 0.05))',
      animation: 'backgroundShift 15s ease-in-out infinite',
      zIndex: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      backgroundAttachment: 'scroll',
      '&::before': {
        animation: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      backgroundAttachment: 'scroll',
      justifyContent: 'flex-start',
      padding: theme.spacing(8, 0, 6),
      overflowX: 'hidden',
      overflowY: 'visible',
      '&::before': {
        animation: 'none',
      },
    },
  },
  topShadow: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '40px',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
    zIndex: 3,
  },
  bottomShadow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '40px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
    zIndex: 3,
  },
  card: {
    maxWidth: 600,
    margin: theme.spacing(0, 2, 2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(0),
    background: 'rgba(10, 15, 30, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(0, 245, 255, 0.2)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: 'linear-gradient(90deg, transparent, #00f5ff, #c77dff, transparent)',
      animation: 'shimmer 3s ease-in-out infinite',
    },
    '&:hover': {
      transform: 'translateY(-10px) scale(1.02)',
      borderColor: 'rgba(0, 245, 255, 0.5)',
      boxShadow: '0 35px 70px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 245, 255, 0.3)',
      background: 'rgba(15, 20, 35, 0.9)',
      '& $name': {
        color: '#00f5ff',
      },
      '& $recommendation': {
        color: '#ffffff',
      },
    },
  },
  cardContent: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 2,
  },
  button: {
    marginTop: 'auto',
    background: 'linear-gradient(135deg, #3f51b5, #5c6bc0)',
    backgroundSize: '200% 200%',
    color: '#ffffff',
    fontWeight: 'bold',
    fontFamily: '"Inter", sans-serif',
    borderRadius: '12px',
    textTransform: 'none',
    fontSize: '0.95rem',
    padding: theme.spacing(1.5, 2),
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    border: '1px solid rgba(63, 81, 181, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    '&:hover': {
      backgroundPosition: '100% 0',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(63, 81, 181, 0.4)',
      borderColor: 'rgba(0, 245, 255, 0.5)',
      '&::before': {
        left: '100%',
      },
    },
  },
  profileButton: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
    background: 'linear-gradient(135deg, #0077b5, #005885)',
    color: '#ffffff',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(0, 119, 181, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0088cc, #006699)',
      transform: 'scale(1.1)',
      boxShadow: '0 4px 15px rgba(0, 119, 181, 0.4)',
    },
  },
  recommendation: {
    flex: '1 1 auto',
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
    lineHeight: 1.7,
    color: 'rgba(255, 255, 255, 0.8)',
    padding: theme.spacing(2),
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    fontStyle: 'italic',
    '&::before': {
      content: '"❝"',
      position: 'absolute',
      top: '-10px',
      left: '10px',
      fontSize: '2rem',
      color: 'rgba(0, 245, 255, 0.6)',
      fontWeight: 'bold',
    },
    '&::after': {
      content: '"❞"',
      position: 'absolute',
      bottom: '-15px',
      right: '10px',
      fontSize: '2rem',
      color: 'rgba(199, 125, 255, 0.6)',
      fontWeight: 'bold',
    },
  },  
  languageIcon: {
    fontSize: '1.1rem',
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
    padding: '2px',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
  },
  workIcon: {
    fontSize: '1.1rem',
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
    color: 'rgba(0, 245, 255, 0.8)',
    transition: 'all 0.3s ease',
  },
  name: {
    fontSize: '1.4rem',
    fontWeight: 700,
    fontFamily: '"JetBrains Mono", monospace',
    background: 'linear-gradient(135deg, #ffffff, rgba(0, 245, 255, 0.8))',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(1),
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  companyInfo: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.95rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 500,
  },
  languageInfo: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    fontFamily: '"Inter", sans-serif',
    padding: theme.spacing(0.5, 1),
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    width: 'fit-content',
  },
  titleHeader: {
    textAlign: 'center',
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 700,
    fontSize: '3rem',
    background: 'linear-gradient(135deg, #00f5ff, #c77dff, #e0aaff)',
    backgroundSize: '300% 300%',
    animation: 'gradientShift 4s ease infinite',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(5),
    position: 'relative',
    zIndex: 2,
    textShadow: 'none',
    '&::after': {
      content: '"</>"',
      position: 'absolute',
      top: '-15px',
      right: '-30px',
      fontSize: '1.8rem',
      color: 'rgba(0, 245, 255, 0.7)',
      opacity: 0.8,
    },
    '&::before': {
      content: '"<"',
      position: 'absolute',
      top: '-15px',
      left: '-30px',
      fontSize: '1.8rem',
      color: 'rgba(199, 125, 255, 0.7)',
      opacity: 0.8,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2.2rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.8rem',
    },
  },
  container: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(0, 2),
    position: 'relative',
    zIndex: 2,
  },
  // Animaciones personalizadas
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
  '@keyframes backgroundShift': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.8 },
  },
  // Estilos para el slider
  sliderContainer: {
    '& .slick-dots': {
      bottom: '-50px',
      '& li button:before': {
        fontSize: '12px',
        color: 'rgba(0, 245, 255, 0.6)',
      },
      '& li.slick-active button:before': {
        color: '#00f5ff',
      },
    },
    '& .slick-prev, & .slick-next': {
      zIndex: 1,
      '&:before': {
        fontSize: '25px',
        color: 'rgba(0, 245, 255, 0.8)',
      },
      '&:hover:before': {
        color: '#00f5ff',
      },
    },
    '& .slick-prev': {
      left: '-50px',
    },
    '& .slick-next': {
      right: '-50px',
    },
  },
}));

const recommendations = [
  {
    id: 1,
    name: 'Ruth-Ann Samuels',
    company: 'Lynk',
    profile: 'https://www.linkedin.com/in/ruthannsamuels',
    recommendation:
      'I am thrilled to provide my highest recommendation for Carlos Aguilera in his role as a Senior Developer. Throughout our collaboration, I\'ve observed his dedication and contributions to our team. What sets Carlos apart is his collaborative spirit and leadership qualities. He excels in fostering a positive team environment by willingly sharing his knowledge and offering guidance to fellow team members. His technical skills allow him to effectively contribute to our development tasks. He demonstrates a good grasp of coding principles and is able to execute his tasks in a timely manner. His willingness to collaborate and work as part of the team is commendable. I am confident that Carlos will continue to excel and make significant contributions wherever his career path leads him.',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'EN',
  },
  {
    id: 2,
    name: 'Matthew Budram',
    company: 'Lynk',
    profile: 'https://www.linkedin.com/in/janesmith',
    recommendation:
      'Carlos has been a part of our engineering team at Lynk. The team has delivered some of the most impressive features for a mobile wallet in the Caribbean. Carlos has consistently demonstrated his skills in Java Spring Boot in a fast-paced environment. I am happy to have worked with Carlos and look forward to seeing him grow from strength to strength!',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'EN',
  },
  {
    id: 3,
    name: 'Yuman J. Montero M.',
    company: 'Galicia Seguros',
    profile: 'https://www.linkedin.com/in/yuman-jared-montero-moreno-4a6983108',
    recommendation: 'Excelente persona con amplios conocimientos en el área de desarrollo.',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'ES',
  },
  {
    id: 4,
    name: 'Matías Daniel Quintana',
    company: 'Galicia Seguros',
    profile: 'https://www.linkedin.com/in/mdquin89',
    recommendation:
      'Ignacio es un excelente profesional, siempre predispuesto a cooperar y dar solución a los problemas existentes. Al margen de ser excelente profesional es una excelente persona, muy cálida y siempre va a hacer lo que este a su alcance para ayudar. Fue un placer y un alivio contar con su cooperación en el proyecto en el cual estuve trabajando dentro de la empresa que lo emplea.',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'ES',
  },
  {
    id: 5,
    name: 'Enrique Jorge Mariani De Camas',
    company: 'Banco Hipotecario',
    profile: 'https://www.linkedin.com/in/henrex',
    recommendation: 'Carlos Ignacio es un profesional de destacadas cualidades técnicas y buen compañero, que disfruta incorporar nuevos conocimientos y trabajar en equipo. Totalmente recomendable.',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'ES',
  },
  {
    id: 6,
    name: 'María Celeste Gomez',
    company: 'Telecom',
    profile: 'https://www.linkedin.com/in/yuman-jared-montero-moreno-4a6983108',
    recommendation: 'Buen compañero, paciente y súper didáctico en busca de la solución más eficaz y eficiente!',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'ES',
  },
  {
    id: 7,
    name: 'Gabriel Villoldo',
    company: 'UAI',
    profile: 'https://www.linkedin.com/in/gabrielvilloldo',
    recommendation: 'Excelente persona, predispuesta siempre a dar lo mejor de sí mismo, con muchísima proactividad. Fue un placer no solo compartir las cursadas de distintas materias sino realizar trabajos prácticos y de investigación con él. Un profesional muy recomendable.',
    linkedinUrl: 'https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/details/recommendations/?detailScreenTabIndex=0',
    language: 'ES',
  },
];

const Recommendations = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <section className={classes.section}>
      <div className={classes.topShadow}></div>    
      <div className={classes.container}>
        <h2 className={classes.titleHeader}>{t('recommendations.title')}</h2>
        <div className={classes.sliderContainer}>
          <Slider {...settings}>
            {recommendations.map((rec) => (
              <div key={rec.id}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <div className={classes.name}>
                      {rec.name}
                      <IconButton 
                        color="primary" 
                        className={classes.profileButton} 
                        href={rec.profile} 
                        target="_blank"
                        size="small"
                      >
                        <LinkedInIcon fontSize="small" />
                      </IconButton>
                    </div>
                    
                    <Typography className={classes.companyInfo}>
                      <WorkIcon className={classes.workIcon} />
                      {rec.company}
                    </Typography>
                    
                    <div className={classes.languageInfo}>
                      <LanguageIcon 
                        className={classes.languageIcon} 
                        style={{ 
                          color: rec.language === 'EN' ? '#3f51b5' : '#e91e63',
                          backgroundColor: rec.language === 'EN' ? 'rgba(63, 81, 181, 0.1)' : 'rgba(233, 30, 99, 0.1)'
                        }} 
                      />
                      {rec.language === 'EN' ? t('recommendations.language.en') : t('recommendations.language.es')}
                    </div>
                    
                    <Typography className={classes.recommendation}>
                      {rec.recommendation.length > 200 ? `${rec.recommendation.substring(0, 200)}...` : rec.recommendation}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      startIcon={<LinkedInIcon />}
                      href={rec.linkedinUrl}
                      target="_blank"
                      fullWidth
                    >
                      {t('recommendations.viewOnLinkedIn')}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </Slider>
        </div>
      </div>
      <div className={classes.bottomShadow}></div>
    </section>
  );
};

export default Recommendations;
